import { useEffect, useState } from "react";
import { api, ApiError, type Goals } from "../lib/api";
import {
  computeGoals,
  type ActivityLevel,
  type Objective,
  type Profile,
  type Sex,
} from "../lib/goals";
import { Spinner } from "../components/Spinner";
import { InfoTooltip } from "../components/InfoTooltip";

const DEFAULT_PROFILE: Profile = {
  sex: "male",
  age: 30,
  heightCm: 179,
  weightKg: 80,
  activity: "light",
  objective: "build",
};

const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: "Sedentary — little to no exercise",
  light: "Light — exercise 1–3 days/week",
  moderate: "Moderate — exercise 3–5 days/week",
  very_active: "Very active — exercise 6–7 days/week",
};

const OBJECTIVE_LABELS: Record<Objective, string> = {
  cut: "Cut — lose fat",
  maintain: "Maintain",
  build: "Build — gain muscle",
};

export function Settings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState<Profile>(DEFAULT_PROFILE);
  const [override, setOverride] = useState(false);
  const [overrideCalories, setOverrideCalories] = useState(0);
  const [overrideProtein, setOverrideProtein] = useState(0);

  useEffect(() => {
    let alive = true;
    api
      .goals()
      .then((r) => {
        if (!alive) return;
        seedFromGoals(r.goals);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  function seedFromGoals(g: Goals) {
    if (g.sex && g.age && g.heightCm && g.weightKg && g.activity && g.objective) {
      setProfile({
        sex: g.sex,
        age: g.age,
        heightCm: g.heightCm,
        weightKg: g.weightKg,
        activity: g.activity,
        objective: g.objective,
      });
    }
    setOverride(g.goalsAuto === false);
    setOverrideCalories(g.caloriesGoal);
    setOverrideProtein(g.proteinGoalG);
  }

  if (loading)
    return (
      <div className="loading">
        <Spinner />
      </div>
    );
  if (error) return <div className="error">Failed to load goals — {error}</div>;

  const recommended = computeGoals(profile);
  const effectiveCalories = override ? overrideCalories : recommended.caloriesGoal;
  const effectiveProtein = override ? overrideProtein : recommended.proteinGoalG;

  return (
    <div className="settings">
      <section className="card">
        <h2>Profile</h2>
        <div className="settings-form">
          <label>
            <span>Sex</span>
            <select
              value={profile.sex}
              onChange={(e) => setProfile({ ...profile, sex: e.target.value as Sex })}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </label>
          <label>
            <span>Age</span>
            <input
              type="number"
              value={profile.age}
              onChange={(e) => setProfile({ ...profile, age: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Height (cm)</span>
            <input
              type="number"
              value={profile.heightCm}
              onChange={(e) => setProfile({ ...profile, heightCm: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Weight (kg)</span>
            <input
              type="number"
              value={profile.weightKg}
              onChange={(e) => setProfile({ ...profile, weightKg: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Activity</span>
            <select
              value={profile.activity}
              onChange={(e) =>
                setProfile({ ...profile, activity: e.target.value as ActivityLevel })
              }
            >
              {(Object.keys(ACTIVITY_LABELS) as ActivityLevel[]).map((a) => (
                <option key={a} value={a}>
                  {ACTIVITY_LABELS[a]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Objective</span>
            <select
              value={profile.objective}
              onChange={(e) => setProfile({ ...profile, objective: e.target.value as Objective })}
            >
              {(Object.keys(OBJECTIVE_LABELS) as Objective[]).map((o) => (
                <option key={o} value={o}>
                  {OBJECTIVE_LABELS[o]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="card">
        <h2>Recommendation</h2>
        <div className="settings-recommendation">
          <div className="settings-stat">
            <span className="settings-stat__label">
              BMR
              <InfoTooltip text="Basal Metabolic Rate — calories your body burns at rest, before any activity (Mifflin-St Jeor formula)" />
            </span>
            <span className="settings-stat__value">{Math.round(recommended.bmr)} kcal</span>
          </div>
          <div className="settings-stat">
            <span className="settings-stat__label">
              TDEE
              <InfoTooltip text="Total Daily Energy Expenditure — your BMR scaled up by your activity level" />
            </span>
            <span className="settings-stat__value">{Math.round(recommended.tdee)} kcal</span>
          </div>
          <div className="settings-stat">
            <span className="settings-stat__label">
              Calorie ceiling
              <InfoTooltip text="Recommended daily calorie ceiling — your TDEE adjusted for your objective (cut/maintain/build). Stay under this." />
            </span>
            <span className="settings-stat__value">{recommended.caloriesGoal} kcal</span>
          </div>
          <div className="settings-stat">
            <span className="settings-stat__label">
              Protein floor
              <InfoTooltip text="Recommended daily protein floor — based on your weight and objective. Aim for at least this much." />
            </span>
            <span className="settings-stat__value">{recommended.proteinGoalG} g</span>
          </div>
        </div>

        <label className="settings-override-toggle">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => {
              const on = e.target.checked;
              setOverride(on);
              if (on) {
                setOverrideCalories(recommended.caloriesGoal);
                setOverrideProtein(recommended.proteinGoalG);
              }
            }}
          />
          <span>Override with custom numbers</span>
        </label>

        {override && (
          <div className="settings-form">
            <label>
              <span>Calorie ceiling</span>
              <input
                type="number"
                value={overrideCalories}
                onChange={(e) => setOverrideCalories(Number(e.target.value))}
              />
            </label>
            <label>
              <span>Protein floor (g)</span>
              <input
                type="number"
                value={overrideProtein}
                onChange={(e) => setOverrideProtein(Number(e.target.value))}
              />
            </label>
          </div>
        )}

        <footer className="settings-footer">
          <button
            className="primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setSaved(false);
              try {
                const r = await api.putGoals({
                  caloriesGoal: Math.round(effectiveCalories),
                  proteinGoalG: Math.round(effectiveProtein),
                  goalsAuto: !override,
                  ...profile,
                });
                seedFromGoals(r.goals);
                setSaved(true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : "Save"}
          </button>
          {saved && <span className="settings-saved">Saved</span>}
        </footer>
      </section>
    </div>
  );
}
